import {
  itemDataUrl,
  itemPhotoSrc,
  itemTitle,
  itemWishText,
  productsKindOf,
  profileLayoutOf,
  type PublicItem,
} from "@fusiqo/vozqi-shared";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { prefillPendingSignupWish } from "../lib/pending-signup";
import { ItemBusiness } from "./item-business";
import { ItemDetails, ItemDetailsSkeleton } from "./item-details";
import { ItemMore } from "./item-more";
import { ProfileActions } from "./profile-actions";
import { tokenFromPath, whatsappHref } from "./share-view";
import { PublicHeader, PublicHeaderSkeleton } from "./public-header";
import { useItemProfile } from "./use-item-profile";
import { VozqiFoot } from "./vozqi-foot";
import { WhatsAppMark } from "./whatsapp-mark";
import { BrandLogo } from "../ui/brand-logo";
// oxlint-disable-next-line import/no-unassigned-import -- the profile's stylesheet: an item's page is a page of its profile
import "./profile.css";

/**
 * THE PUBLIC PAGE OF ONE ITEM (spec compartir producto §7): what
 * a customer sees after tapping a link the business sent over WhatsApp.
 *
 * NOT THE APP. No router, no session, no kit: this entry (share.html) loads
 * React, the theme's tokens and this file, and nothing else -- somebody on
 * mobile data downloads the page they asked for. The data comes from the
 * Worker on the same origin (`/p/api/<token>`), so there is no CORS and no
 * storage address ever reaches the browser.
 *
 * THE PHOTOS SLIDE, AND OPEN IN PLACE, never in a new tab: swipe, arrows
 * and keys, dots and a counter; a tap opens the same slider full screen,
 * closed with «Cerrar» at the bottom (the app never draws a corner ✕) or Esc.
 * WhatsApp is a floating button at the bottom right, not a footer bar.
 *
 * A PAGE OF THE PROFILE, keeping the public profile's template: when the business's
 * profile is published, the item reads as one of its pages -- the profile's
 * bar with «‹ Ver perfil», its sign folded small over the item, the photos
 * beside a panel with the price, the options and the profile's own «Pedir
 * servicio» (which opens with the item already written in), then a few more
 * of its services or products. An item shared alone is the photos, the
 * panel and WhatsApp.
 */
type State =
  | { kind: "loading" }
  | { kind: "gone" }
  | { kind: "failed" }
  | { kind: "ready"; view: PublicItem };

export function SharePage() {
  const token = tokenFromPath(window.location.pathname);
  const [state, setState] = useState<State>(token === null ? { kind: "gone" } : { kind: "loading" });

  const load = useCallback(async () => {
    if (token === null) return;
    setState({ kind: "loading" });
    try {
      const response = await fetch(itemDataUrl(token));
      if (response.status === 404) return setState({ kind: "gone" });
      if (!response.ok) return setState({ kind: "failed" });
      setState({ kind: "ready", view: (await response.json()) as PublicItem });
    } catch {
      setState({ kind: "failed" });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (state.kind === "ready") document.title = itemTitle(state.view);
  }, [state]);

  if (state.kind === "loading") return <Skeleton />;
  if (state.kind === "gone") {
    return (
      <Notice title="Este enlace ya no está activo">
        <p>Pídele a quien te lo envió que lo comparta de nuevo.</p>
      </Notice>
    );
  }
  if (state.kind === "failed" || token === null) {
    return (
      <Notice title="No pudimos cargar esto">
        <p>Revisa tu conexión e inténtalo otra vez.</p>
        <button type="button" className="share-retry" onClick={() => void load()}>
          Reintentar
        </button>
      </Notice>
    );
  }
  return <Item view={state.view} token={token} />;
}

function Item({ view, token }: { view: PublicItem; token: string }) {
  const { variants } = view.item;
  // Several options wait to be chosen, «Desde» the cheapest meanwhile; one is chosen already.
  const [chosen, setChosen] = useState<number | null>(variants.length > 1 ? null : 0);
  const [viewing, setViewing] = useState<number | null>(null);
  const slug = view.business.shopSlug;
  const profile = useItemProfile(slug);
  const shop = profile.shop;
  const variantName = chosen === null ? null : (variants[chosen]?.name ?? null);
  const whatsapp = whatsappHref(view, variantName);
  const srcOf = (id: string, size: "thumb" | "full"): string => itemPhotoSrc(token, id, size);
  const logoSrc = view.business.hasLogo ? `/p/${token}/logo` : null;
  const profileHref = slug === null ? null : `/t/${slug}`;
  const layout = shop === null ? null : profileLayoutOf(shop.profile);
  const requestToken = shop?.profile.requestToken ?? null;
  // A product is ordered, a service asked for: the same request either way, with the item written in.
  const requestLabel = view.item.kind === "SERVICE" ? "Pedir servicio" : "Pedir";

  // WhatsApp is the floating button's alone; without a request, the panel
  // points at it rather than standing empty.
  const actions =
    profile.status === "loading" ? (
      <div className="item-actions-skeleton share-skeleton" aria-hidden="true" />
    ) : requestToken === null ? (
      whatsapp === null ? null : <p className="item-whatsapp-hint">Escríbele por WhatsApp con el botón verde</p>
    ) : (
      <ProfileActions
        token={requestToken}
        requestLabel={requestLabel}
        onRequest={() => prefillPendingSignupWish(requestToken, itemWishText(view.item.name, variantName))}
      />
    );

  return (
    <div className={whatsapp === null ? "item-page" : "item-page item-page--with-fab"}>
      <PublicHeader
        name={view.business.name}
        logoSrc={logoSrc}
        heading={false}
        shopHref={profile.status === "ready" || profile.status === "failed" ? profileHref : null}
        backLabel={layout?.shopWord === true ? "‹ Ver tienda" : "‹ Ver perfil"}
        quiet={profile.status === "loading"}
      />
      <main className="item-layout">
        {view.item.photos.length > 0 ? (
          <Photos photos={view.item.photos} srcOf={srcOf} name={view.item.name} onOpen={setViewing} />
        ) : (
          // NO PHOTO: the item's initial, as its card in the shop shows it
          // -- the page keeps its shape either way.
          <div className="share-gallery">
            <span className="share-photo-initial" aria-hidden="true">
              {view.item.name.trim().charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <ItemDetails
          view={view}
          chosen={chosen}
          onChoose={setChosen}
          actions={actions}
          business={shop !== null && profileHref !== null ? <ItemBusiness shop={shop} href={profileHref} /> : null}
        />
      </main>

      {shop !== null && slug !== null && profileHref !== null && layout !== null ? (
        <ItemMore
          shop={shop}
          slug={slug}
          kind={view.item.kind === "SERVICE" ? "SERVICE" : productsKindOf(layout)}
          token={token}
          profileHref={profileHref}
        />
      ) : null}

      <VozqiFoot slug={slug} page="articulo" />

      {whatsapp !== null && viewing === null && (
        <a
          className="share-fab"
          href={whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Escribir por WhatsApp"
          title="Escribir por WhatsApp"
        >
          <WhatsAppMark />
        </a>
      )}

      {viewing !== null && (
        <Viewer
          photos={view.item.photos}
          srcOf={srcOf}
          name={view.item.name}
          start={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

/**
 * Where a photo's bytes are: each public link has its own Worker paths
 * (`/p/…/foto/…`, `/e/…/foto/…`), and the slider draws either.
 */
export type PhotoSrc = (id: string, size: "thumb" | "full") => string;

/** The inline gallery: 4:3, whole photos over a quiet surface, a tap opens the viewer. */
export function Photos({
  photos,
  srcOf,
  name,
  onOpen,
}: {
  photos: string[];
  srcOf: PhotoSrc;
  name: string;
  onOpen: (index: number) => void;
}) {
  return (
    <div className="share-gallery">
      <Slider photos={photos} srcOf={srcOf} name={name} size="thumb" start={0} onOpen={onOpen} />
    </div>
  );
}

/**
 * The full-screen viewer: the same slider, bigger, full photos, over a dark
 * backdrop. A native <dialog> -- Esc, the top layer and a focus trap come
 * with it; «Cerrar» at the bottom is the app's way out.
 */
export function Viewer({
  photos,
  srcOf,
  name,
  start,
  onClose,
}: {
  photos: string[];
  srcOf: PhotoSrc;
  name: string;
  start: number;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (element !== null && !element.open) element.showModal();
    // The page behind must not scroll under a finger that swipes photos.
    const overflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = overflow;
    };
  }, []);

  return (
    <dialog
      ref={dialog}
      className="share-viewer"
      aria-label={`Fotos de ${name}`}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="share-viewer-stage">
        <Slider photos={photos} srcOf={srcOf} name={name} size="full" start={start} onOpen={null} />
      </div>
      <div className="share-viewer-foot">
        <button type="button" className="share-viewer-close" onClick={onClose} autoFocus>
          Cerrar
        </button>
      </div>
    </dialog>
  );
}

/**
 * Up to five photos, swiped with CSS scroll-snap -- no library. The index is
 * READ from the scroll position, never pushed into it except by the arrows,
 * the dots and the keys, so a swipe and a click can never disagree about
 * which photo is showing. With one photo there is nothing to move: no
 * arrows, no dots, no counter.
 */
function Slider({
  photos,
  srcOf,
  name,
  size,
  start,
  onOpen,
}: {
  photos: string[];
  srcOf: PhotoSrc;
  name: string;
  size: "thumb" | "full";
  start: number;
  onOpen: ((index: number) => void) | null;
}) {
  const strip = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(start);
  const several = photos.length > 1;

  // Opening the viewer on the photo that was tapped: placed, not animated. A
  // frame later, because the viewer's <dialog> opens in its own effect, which
  // runs after this one -- until then the strip has no width.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const element = strip.current;
      if (element !== null) element.scrollLeft = start * element.clientWidth;
    });
    return () => cancelAnimationFrame(frame);
  }, [start]);

  const onScroll = (): void => {
    const element = strip.current;
    if (element === null || element.clientWidth === 0) return;
    setCurrent(Math.round(element.scrollLeft / element.clientWidth));
  };

  const go = (index: number): void => {
    const element = strip.current;
    if (element === null) return;
    const target = Math.max(0, Math.min(photos.length - 1, index));
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    element.scrollTo({ left: target * element.clientWidth, behavior: still ? "auto" : "smooth" });
  };

  const onKey = (event: { key: string; preventDefault: () => void }): void => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      go(current - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      go(current + 1);
    }
  };

  // In the viewer the keys work wherever the focus is (it starts on «Cerrar»);
  // inline, only while the slider itself has the focus.
  useEffect(() => {
    if (size !== "full" || !several) return;
    const listener = (event: globalThis.KeyboardEvent): void => onKey(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  });

  return (
    <div
      className={`share-slider share-slider--${size === "full" ? "viewer" : "inline"}`}
      role="region"
      aria-roledescription="carrusel"
      aria-label={`Fotos de ${name}`}
      tabIndex={several && size === "thumb" ? 0 : -1}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (size === "thumb") onKey(event);
      }}
    >
      <div className="share-strip" ref={strip} onScroll={onScroll}>
        {photos.map((id, index) => (
          <div
            key={id}
            className="share-slide"
            role="group"
            aria-roledescription="foto"
            aria-label={`${String(index + 1)} de ${String(photos.length)}`}
          >
            <Photo
              srcOf={srcOf}
              id={id}
              size={size}
              alt={name}
              eager={index === start}
              onOpen={onOpen === null ? null : () => onOpen(index)}
            />
          </div>
        ))}
      </div>

      {several && (
        <>
          <span className="share-counter tabular" aria-hidden="true">
            {current + 1} / {photos.length}
          </span>
          <button
            type="button"
            className="share-arrow share-arrow--prev"
            aria-label="Foto anterior"
            disabled={current === 0}
            onClick={() => go(current - 1)}
          >
            <Chevron direction="left" />
          </button>
          <button
            type="button"
            className="share-arrow share-arrow--next"
            aria-label="Foto siguiente"
            disabled={current === photos.length - 1}
            onClick={() => go(current + 1)}
          >
            <Chevron direction="right" />
          </button>
          <div className="share-dots">
            {photos.map((id, index) => (
              <button
                key={id}
                type="button"
                className="share-dot"
                aria-label={`Ver la foto ${String(index + 1)}`}
                aria-current={index === current ? "true" : undefined}
                onClick={() => go(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * One photo, whole (`object-fit: contain`): a portrait, a landscape and a logo
 * on white all sit on the same quiet surface. A thumbnail that was never made
 * (a photo from before thumbnails) falls back to the full photo, once.
 */
export function Photo({
  srcOf,
  id,
  size,
  alt,
  eager,
  onOpen,
}: {
  srcOf: PhotoSrc;
  id: string;
  size: "thumb" | "full";
  alt: string;
  eager: boolean;
  onOpen: (() => void) | null;
}) {
  const image = (
    <img
      src={srcOf(id, size)}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      draggable={false}
      onError={(event) => {
        const full = srcOf(id, "full");
        if (!event.currentTarget.src.endsWith(full)) event.currentTarget.src = full;
      }}
    />
  );
  if (onOpen === null) return image;
  return (
    <button type="button" className="share-photo" aria-label="Ver la foto en grande" onClick={onOpen}>
      {image}
    </button>
  );
}

function Skeleton() {
  return (
    <div className="item-page" aria-busy="true" aria-label="Cargando">
      <PublicHeaderSkeleton />
      <main className="item-layout">
        <div className="share-gallery">
          <div className="share-skeleton share-skeleton--photo" />
        </div>
        <ItemDetailsSkeleton />
      </main>
    </div>
  );
}

/** `footHref` is the «Hecho con VozQI» link, tagged per page; an item's by default. */
export function Notice({
  title,
  children,
  footHref = "https://vozqi.com/?utm_source=producto",
}: {
  title: string;
  children?: ReactNode;
  footHref?: string;
}) {
  return (
    <div className="share share-notice">
      <main>
        <h1>{title}</h1>
        {children}
      </main>
      <footer className="share-foot">
        <a href={footHref} rel="noopener">
          Hecho con <BrandLogo height={16} />
        </a>
      </footer>
    </div>
  );
}

function Chevron({ direction }: { direction: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path
        d={direction === "left" ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
